import Extent from '@arcgis/core/geometry/Extent.js';
import Polyline from '@arcgis/core/geometry/Polyline.js';
import type Point from '@arcgis/core/geometry/Point.js';
import type SpatialReference from '@arcgis/core/geometry/SpatialReference.js';
import { ensureFeatureLayer } from './layers';

const QUANTUM = 0.5;
const SEARCH_PAD_FACTOR = 1.5;
const SEARCH_PAD_MIN = 500;
const SEARCH_PAD_MAX = 5000;
const SNAP_MAX_DEFAULT = 200;
const QUERY_FEATURE_CAP = 4000;

type NodeId = number;
type Edge = { to: NodeId; w: number };

class MinHeap {
  private items: Array<{ key: number; value: NodeId }> = [];
  push(value: NodeId, key: number): void {
    this.items.push({ key, value });
    this.bubbleUp(this.items.length - 1);
  }
  pop(): NodeId | null {
    if (this.items.length === 0) return null;
    const top = this.items[0].value;
    const last = this.items.pop()!;
    if (this.items.length > 0) {
      this.items[0] = last;
      this.sinkDown(0);
    }
    return top;
  }
  get size(): number {
    return this.items.length;
  }
  private bubbleUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[i].key >= this.items[parent].key) break;
      [this.items[i], this.items[parent]] = [this.items[parent], this.items[i]];
      i = parent;
    }
  }
  private sinkDown(i: number): void {
    const n = this.items.length;
    while (true) {
      let smallest = i;
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      if (l < n && this.items[l].key < this.items[smallest].key) smallest = l;
      if (r < n && this.items[r].key < this.items[smallest].key) smallest = r;
      if (smallest === i) break;
      [this.items[i], this.items[smallest]] = [this.items[smallest], this.items[i]];
      i = smallest;
    }
  }
}

export type AstarInput = {
  fromPoint: Point;
  toPoint: Point;
  networkLayerId: string;
  maxSnapDistance?: number;
};

export type AstarResult = {
  polyline: Polyline;
  fromSnap: { x: number; y: number };
  toSnap: { x: number; y: number };
  nodes_explored: number;
};

export async function shortestPathOverNetwork(input: AstarInput): Promise<AstarResult> {
  const fromSr = input.fromPoint.spatialReference;
  const toSr = input.toPoint.spatialReference;
  if (!fromSr || !toSr) {
    throw new Error('compute_shortest_path: endpoints lack a spatial reference');
  }
  if (fromSr.wkid !== toSr.wkid) {
    throw new Error(
      'compute_shortest_path: endpoints are in different spatial references — reproject first',
    );
  }

  const layer = await ensureFeatureLayer(input.networkLayerId);
  const sr: SpatialReference = fromSr;
  const snapMax =
    input.maxSnapDistance != null && input.maxSnapDistance > 0
      ? input.maxSnapDistance
      : SNAP_MAX_DEFAULT;

  const dxStraight = Math.hypot(
    input.fromPoint.x - input.toPoint.x,
    input.fromPoint.y - input.toPoint.y,
  );
  const pad = Math.min(SEARCH_PAD_MAX, Math.max(SEARCH_PAD_MIN, dxStraight * SEARCH_PAD_FACTOR));

  const bbox = new Extent({
    xmin: Math.min(input.fromPoint.x, input.toPoint.x) - pad,
    ymin: Math.min(input.fromPoint.y, input.toPoint.y) - pad,
    xmax: Math.max(input.fromPoint.x, input.toPoint.x) + pad,
    ymax: Math.max(input.fromPoint.y, input.toPoint.y) + pad,
    spatialReference: sr,
  });

  const rs = await layer.queryFeatures({
    geometry: bbox,
    spatialRelationship: 'intersects',
    where: '1=1',
    returnGeometry: true,
    outFields: [layer.objectIdField || 'objectid'],
    outSpatialReference: sr,
    num: QUERY_FEATURE_CAP,
  });

  if (rs.features.length === 0) {
    throw new Error(
      `compute_shortest_path: no features in "${input.networkLayerId}" within ${Math.round(pad)}m of the endpoints`,
    );
  }

  const nodes: Array<{ x: number; y: number }> = [];
  const nodeIdByKey = new Map<string, NodeId>();
  const adj = new Map<NodeId, Edge[]>();

  function nodeFor(x: number, y: number): NodeId {
    const key = `${Math.round(x / QUANTUM)}|${Math.round(y / QUANTUM)}`;
    let id = nodeIdByKey.get(key);
    if (id == null) {
      id = nodes.length;
      nodes.push({ x, y });
      nodeIdByKey.set(key, id);
      adj.set(id, []);
    }
    return id;
  }

  function addEdge(a: NodeId, b: NodeId, w: number): void {
    if (a === b || w <= 0) return;
    adj.get(a)!.push({ to: b, w });
    adj.get(b)!.push({ to: a, w });
  }

  for (const feat of rs.features) {
    const g = feat.geometry as Polyline | undefined;
    if (!g || g.type !== 'polyline') continue;
    for (const path of g.paths) {
      for (let i = 1; i < path.length; i++) {
        const [x1, y1] = path[i - 1];
        const [x2, y2] = path[i];
        addEdge(nodeFor(x1, y1), nodeFor(x2, y2), Math.hypot(x2 - x1, y2 - y1));
      }
    }
  }

  if (nodes.length === 0) {
    throw new Error('compute_shortest_path: empty road graph in search area');
  }

  const fromIdx = nearestNode(nodes, input.fromPoint.x, input.fromPoint.y);
  const toIdx = nearestNode(nodes, input.toPoint.x, input.toPoint.y);
  const fromGap = Math.hypot(
    nodes[fromIdx].x - input.fromPoint.x,
    nodes[fromIdx].y - input.fromPoint.y,
  );
  const toGap = Math.hypot(
    nodes[toIdx].x - input.toPoint.x,
    nodes[toIdx].y - input.toPoint.y,
  );
  if (fromGap > snapMax) {
    throw new Error(
      `compute_shortest_path: start point is ${fromGap.toFixed(0)}m from the nearest "${input.networkLayerId}" feature (max ${snapMax}m)`,
    );
  }
  if (toGap > snapMax) {
    throw new Error(
      `compute_shortest_path: end point is ${toGap.toFixed(0)}m from the nearest "${input.networkLayerId}" feature (max ${snapMax}m)`,
    );
  }

  const cameFrom = new Map<NodeId, NodeId>();
  const gScore = new Map<NodeId, number>();
  const closed = new Set<NodeId>();
  gScore.set(fromIdx, 0);
  const open = new MinHeap();
  open.push(fromIdx, heuristic(nodes[fromIdx], nodes[toIdx]));

  let found = false;
  let explored = 0;
  while (open.size > 0) {
    const current = open.pop()!;
    if (closed.has(current)) continue;
    closed.add(current);
    explored += 1;
    if (current === toIdx) {
      found = true;
      break;
    }
    const currentG = gScore.get(current) ?? Infinity;
    for (const edge of adj.get(current) ?? []) {
      if (closed.has(edge.to)) continue;
      const tentativeG = currentG + edge.w;
      const knownG = gScore.get(edge.to);
      if (knownG == null || tentativeG < knownG) {
        cameFrom.set(edge.to, current);
        gScore.set(edge.to, tentativeG);
        open.push(edge.to, tentativeG + heuristic(nodes[edge.to], nodes[toIdx]));
      }
    }
  }

  if (!found) {
    throw new Error(
      'compute_shortest_path: no path found in road network within search extent (endpoints may be in disconnected components)',
    );
  }

  const pathNodes: NodeId[] = [toIdx];
  let cur = toIdx;
  while (cur !== fromIdx) {
    const prev = cameFrom.get(cur);
    if (prev == null) {
      throw new Error('compute_shortest_path: path reconstruction failed');
    }
    pathNodes.push(prev);
    cur = prev;
  }
  pathNodes.reverse();

  const coords: number[][] = [[input.fromPoint.x, input.fromPoint.y]];
  for (const id of pathNodes) coords.push([nodes[id].x, nodes[id].y]);
  coords.push([input.toPoint.x, input.toPoint.y]);

  const polyline = new Polyline({ paths: [coords], spatialReference: sr });

  return {
    polyline,
    fromSnap: { x: nodes[fromIdx].x, y: nodes[fromIdx].y },
    toSnap: { x: nodes[toIdx].x, y: nodes[toIdx].y },
    nodes_explored: explored,
  };
}

function nearestNode(nodes: Array<{ x: number; y: number }>, x: number, y: number): NodeId {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < nodes.length; i++) {
    const dx = nodes[i].x - x;
    const dy = nodes[i].y - y;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function heuristic(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
